import type { MIMLO, Module, Programme } from "@/types";

export interface CasLoChange {
  moduleId: string;
  moduleCode: string;
  moduleTitle: string;
  casComponentCode: string;
  mimloId: string;
  mimloIndex: number;
  originalText: string;
  currentText: string;
  /** Why the wording differs, when somebody recorded it. */
  note?: string;
}

function normalizeText(value: string): string {
  return (value ?? "").trim().replace(/\s+/g, " ");
}

function toMimloArray(value: unknown): MIMLO[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => {
      if (item && typeof item === "object") {
        const rec = item as Record<string, unknown>;
        return {
          id: String(rec.id ?? ""),
          text: String(rec.text ?? ""),
        } as MIMLO;
      }
      return null;
    })
    .filter((item): item is MIMLO => Boolean(item && item.id));
}

/**
 * The outcomes of one CAS module whose wording no longer matches the
 * component's, in the module's order. Empty for any other module.
 */
export function getModuleCasLoChanges(module: Module): CasLoChange[] {
  if (module.casSource !== true) {
    return [];
  }
  const currentMimlos = toMimloArray(module.mimlos ?? []);
  const originalById = new Map(
    toMimloArray(module.casOriginalMimlos).map((m) => [m.id, normalizeText(m.text ?? "")]),
  );
  const notes = new Map(
    (module.casDeviationNotes ?? []).map((entry) => [entry.mimloId, entry.note]),
  );
  return currentMimlos.flatMap((mimlo, index) => {
    const originalText = originalById.get(mimlo.id);
    const currentText = normalizeText(mimlo.text ?? "");
    if (originalText === undefined || currentText === originalText) {
      return [];
    }
    return [
      {
        moduleId: module.id,
        moduleCode: String(module.code ?? ""),
        moduleTitle: String(module.title ?? ""),
        casComponentCode: String((module.casComponentCode ?? module.code ?? "") as string),
        mimloId: mimlo.id,
        mimloIndex: index,
        originalText,
        currentText,
        ...(notes.get(mimlo.id) ? { note: notes.get(mimlo.id) } : {}),
      },
    ];
  });
}

export function getCasLoChanges(programme: Programme): CasLoChange[] {
  return ((programme.modules ?? []) as Module[]).flatMap(getModuleCasLoChanges);
}
